<script>
  import { getContext } from "svelte";
  import { dist } from "vector";
  import merge from "lodash/merge";
  import cloneDeep from "lodash/cloneDeep";

  import TextInput from "#lib/sidebar/TextInput.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import { getPreFabricationPrice, getItemSurchargePrice } from "@local/lamina-core";
  import groupItems from "@local/extensions/collections/group-items.js";
  import orderProductItems from "#src/extensions/order-product-items.js";
  import { edgeOffsetPoint } from "@local/extensions/geometry/edge-offset-center.js";

  export let group;
  export let items;
  export let types;
  export let itemPrices;
  export let job;

  const selectedProducts = getContext("selectedProducts");
  const fabrications = getContext("fabrications");
  const categories = getContext("categories");
  const supplier = getContext("supplier");
  const edgeTreatments = getContext("edgeTreatments");
  const productLists = getContext("productLists");
  const priceEntries = getContext("priceEntries");

  const unitOptionLabels = {
    Mixed: "Mixed",
    sqft: "Sq. Ft.",
    sqin: "Sq. In.",
    in: "Inches",
    m2: "Sq. Meters",
    item: "Item",
    item_price: "% of Item Price",
    each: "Each",
    ft: "Feet",
    m: "Meters",
  };

  const unitOptions = ["item", "sqft", "sqin", "m2"];
  const scUnitOptions = ["item", "item_price", "sqft", "sqin", "m2"];

  let entry = {
    unit_price: null,
    unit: "sqft",
    fabrication_price_entries: [],
    edge_treatment_price_entries: [],
  };

  let scPrices = [];

  const fabIds = {
    "circular-hole": "4b782348-f2d6-5e5c-891c-eac7607ed815",
    "rectangular-hole": "77facaa3-1420-56cf-b83a-93c87ea024f5",
    "edge-fabrication": "a3690b99-0157-58de-b3ae-8742251079f9",
    "corner-fabrication": "cb40dd9d-dbc3-58d2-b5ee-a7b60c1e052c",
  };

  $: productList = $productLists?.find((pl) => pl.id === job.product_list_id);
  $: productGroup = $group?.project_type === "product";
  $: grouped = groupItems(items);
  $: ordered = orderProductItems(grouped, types, productGroup, $categories, $supplier);
  $: itemIndices = getItemIndices(ordered);
  $: selectedProductList = Object.keys($selectedProducts)
    .sort((a, b) => itemIndices[a] - itemIndices[b])
    .map((id) => $group?.items[id])
    .filter((item) => item);
  $: surcharges = $group?.surcharges
    ? $group.surcharges
    : presentSurcharges(items, $supplier, productList, $priceEntries);
  $: surchargedProds = getSurchargedPriceTiers(surcharges);
  $: affectedProducts = getAffectedProducts(surcharges);
  $: selectedSurcharges = getSelectedSurcharges(surcharges, selectedProductList);
  $: makeCompositePriceEntry(selectedProductList);
  $: fabPrices = getFabPrices(entry);
  $: etPrices = getEtPrices(entry);

  function presentSurcharges(items, supplier, productList, priceEntries) {
    const types = items.reduce((acc, item) => {
      acc[item.type_id] = true;
      return acc;
    }, {});

    const prods = Object.keys(types).reduce((acc, typeId) => {
      const type = $group.types[typeId];
      acc[type?.product_id] = true;
      return acc;
    }, {});

    const surcharges = supplier.products.filter((p) => p.application === "surcharge");

    return surcharges.filter((surcharge) => {
      return (
        surcharge.data?.affected_products?.some((pid) => prods[pid]) &&
        priceEntries[surcharge.id]?.[productList?.id]
      );
    });
  }

  function getSelectedSurcharges(surcharges, selectedProductList) {
    const selected = {};

    selectedProductList.forEach((item) => {
      const type = $group?.types[item.type_id];

      surcharges.forEach((surcharge, sIdx) => {
        if (
          affectedProducts[surcharge.id]?.[type?.product_id] ||
          item.surcharge_price_overrides?.[sIdx] != null ||
          item.surcharge_prices?.[sIdx] != null
        ) {
          selected[surcharge.id] = true;
        }
      });
    });

    return selected;
  }

  function getAffectedProducts(surcharges) {
    const affected = {};

    surcharges.forEach((surcharge) => {
      affected[surcharge.id] = {};
      surcharge.data?.affected_products?.forEach((pid) => {
        affected[surcharge.id][pid] = true;
      });
    });

    return affected;
  }

  function addMerge(target, source) {
    Object.keys(source).forEach((key) => {
      if (target[key] == undefined) {
        target[key] = source[key];
      } else {
        target[key] += source[key];
      }
    });
  }

  function getItemIndices(ordered) {
    const indices = {};
    let index = 0;
    ordered.forEach((type) => {
      type.items.forEach((item) => {
        indices[item.id] = index++;
      });
    });
    return indices;
  }

  function addPresentFab(obj, fab, item) {
    let id = fab.fab_id != null ? fab.fab_id : fab.type;

    if (obj[id] != undefined) {
      obj[id] += item.quantity;
    } else {
      obj[id] = item.quantity;
    }
  }

  function defScPrice(surcharge, item, prods, basis) {
    const pe = $priceEntries[surcharge.id]?.[productList?.id]?.[0];

    const type = $group?.types[item?.type_id];
    const mp = prods?.[type?.product_id] || 1;

    let unit_price = null;
    if (affectedProducts[surcharge.id]?.[type?.product_id]) {
      unit_price = pe?.unit_price != null ? pe.unit_price * mp : null;
      if (basis?.unit_price != null) unit_price = basis.unit_price;
    }

    return {
      unit_price,
      unit: pe?.unit ?? "sqft",
    };
  }

  function getPresentFabs(item) {
    const all = {};

    if (!item.data.fabrications) return all;

    item.data.fabrications.voids?.forEach((v) => {
      addPresentFab(all, v, item);
    });

    item.data.fabrications.edge?.forEach((e) => {
      addPresentFab(all, e, item);
    });

    item.data.fabrications.corner?.forEach((c) => {
      addPresentFab(all, c, item);
    });

    item.data.fabrications.virtual?.forEach((v) => {
      addPresentFab(all, v, item);
    });

    item.data.fabrications.additive?.forEach((a) => {
      addPresentFab(all, a, item);
    });

    return all;
  }

  function getPresentEdgeTreatments(item) {
    const all = {};

    if (!item.data.fabrications) return all;

    item.data.fabrications.edge_treatment?.forEach((et) => {
      if (et != null) all[et] = true;
    });

    return all;
  }

  function makeCompositePriceEntry(selectedProductList) {
    if (!selectedProductList.length) {
      entry = {
        unit_price: null,
        unit: "sqft",
        fabrication_price_entries: [],
        edge_treatment_price_entries: [],
      };
      scPrices = surcharges.map((s, i) => defScPrice(s));
      return;
    }

    const [firstItem, ...rest] = selectedProductList;
    const unit_price = firstItem.price_override_basis.unit_price ?? null;
    const scp = surcharges.map((s, i) =>
      defScPrice(s, firstItem, surchargedProds[i], firstItem.surcharge_price_override_basis?.[i]),
    );
    const hasArea = firstItem.width && firstItem.height;
    const unit = firstItem.price_override_basis.unit ?? (hasArea ? "sqft" : "item");
    const fabs = getPresentFabs(firstItem);
    const edgeTreatments = getPresentEdgeTreatments(firstItem);

    const e = {
      unit_price,
      unit,
      fabrication_price_entries: [],
      edge_treatment_price_entries: [],
    };

    rest.forEach((item) => {
      const itemPrice = item.price_override_basis.unit_price ?? null;
      surcharges.forEach((s, i) => {
        const def = defScPrice(s, item, surchargedProds[i], item.surcharge_price_override_basis?.[i]);
        if (def.unit_price !== scp[i].unit_price) {
          scp[i].unit_price = "Mixed";
        }

        if (def.unit !== scp[i].unit) {
          scp[i].unit = "Mixed";
        }
      });

      if (itemPrice !== unit_price) {
        e.unit_price = "Mixed";
      }

      const hasArea = item.width && item.height;
      const itemUnit = item.price_override_basis.unit ?? (hasArea ? "sqft" : "item");
      if (itemUnit !== unit) {
        e.unit = "Mixed";
      }

      const f = getPresentFabs(item);
      const et = getPresentEdgeTreatments(item);

      addMerge(fabs, f);
      merge(edgeTreatments, et);
    });

    const fabBasis = selectedProductList.reduce((acc, item) => {
      item.price_override_basis.fabrication_price_entries?.forEach((fab) => {
        if (acc[fab.fabrication_id]) {
          if (fab.unit_price !== acc[fab.fabrication_id].unit_price) {
            acc[fab.fabrication_id].unit_price = "Mixed";
          }

          if (fab.unit !== acc[fab.fabrication_id].unit) {
            acc[fab.fabrication_id].unit = "Mixed";
          }
        } else {
          acc[fab.fabrication_id] = fab;
        }
      });

      return acc;
    }, {});

    const etBasis = selectedProductList.reduce((acc, item) => {
      item.price_override_basis.edge_treatment_price_entries?.forEach((et) => {
        if (acc[et.edge_treatment_id]) {
          if (et.unit_price !== acc[et.edge_treatment_id].unit_price) {
            acc[et.edge_treatment_id].unit_price = "Mixed";
          }

          if (et.unit !== acc[et.edge_treatment_id].unit) {
            acc[et.edge_treatment_id].unit = "Mixed";
          }
        } else {
          acc[et.edge_treatment_id] = et;
        }
      });

      return acc;
    }, {});

    e.fabrication_price_entries = Object.entries(fabs).map(([fabId, quantity]) => {
      const fabrication_id = fabIds[fabId] ?? fabId;
      const unit_price = fabBasis[fabrication_id]?.unit_price ?? null;
      const unit = fabBasis[fabrication_id]?.unit ?? "each";

      return { fabrication_id, unit_price, unit, quantity };
    });

    e.edge_treatment_price_entries = Object.keys(edgeTreatments).map((etid) => {
      const unit_price = etBasis[etid]?.unit_price ?? null;
      const unit = etBasis[etid]?.unit ?? "in";

      return { edge_treatment_id: etid, unit_price, unit };
    });

    entry = e;
    scPrices = scp;
  }

  function parser(v) {
    if (v === "" || v === undefined || v === null) return null;
    if (typeof v !== "string") throw new Error("Invalid currency value");

    const num = v.match(/\d+(\.\d*)?|\.\d+/);
    if (!num) throw new Error("Invalid currency value");

    const d = parseFloat(num[0]);
    if (!Number.isFinite(d)) {
      throw new Error("Invalid currency value");
    }

    return d;
  }

  function formatter(v) {
    if (v === "Mixed") return "Mixed";
    if (!Number.isFinite(v)) return "";
    return v.toFixed(2);
  }

  function getFabPrices(entry) {
    if (!entry) return {};

    return entry.fabrication_price_entries.reduce((acc, e) => {
      acc[e.fabrication_id] = e;
      return acc;
    }, {});
  }

  function getEtPrices(entry) {
    if (!entry) return {};

    return entry.edge_treatment_price_entries.reduce((acc, e) => {
      acc[e.edge_treatment_id] = {
        unit_price: e.unit_price ?? 0,
        unit: e.unit ?? "in",
      };
      return acc;
    }, {});
  }

  function linearPrice(entry, lenInches) {
    if (entry.unit === "in") {
      return entry.unit_price * lenInches;
    } else if (entry.unit === "ft") {
      return entry.unit_price * (lenInches * 0.08333333333);
    } else if (entry.unit === "m") {
      return entry.unit_price * (lenInches * 0.0254);
    } else if (entry.unit === "mm") {
      return entry.unit_price * (lenInches * 25.4);
    } else if (entry.unit === "cm") {
      return entry.unit_price * (lenInches * 2.54);
    }
    return 0;
  }

  function fabricationPrice(item, fab, fabId) {
    const entry = fabPrices[fabId];
    if (!entry?.unit_price || !entry?.unit) return 0;

    if (entry.unit === "each") {
      return entry.unit_price;
    }

    if (fab.type === "two-point") {
      const e1 = item.cache.edges[fab.reference1.loop][fab.reference1.edge];
      const a = edgeOffsetPoint(e1, fab.reference1);
      const e2 = item.cache.edges[fab.reference2.loop][fab.reference2.edge];
      const b = edgeOffsetPoint(e2, fab.reference2);
      const d = dist(a.center, b.center);
      if (d) return linearPrice(entry, d);
    }

    return 0;
  }

  function getItemFabPrice(item, fabPrices) {
    if (!item?.data.fabrications) return 0;

    let price = 0;

    item.data.fabrications.voids?.forEach((v) => {
      const fabId = v.fab_id || fabIds[v.type];
      price += fabricationPrice(item, v, fabId);
    });

    item.data.fabrications.edge?.forEach((e) => {
      const fabId = e.fab_id || fabIds[e.type];
      price += fabricationPrice(item, v, fabId);
    });

    item.data.fabrications.corner?.forEach((c) => {
      const fabId = c.fab_id || fabIds[c.type];
      price += fabricationPrice(item, v, fabId);
    });

    item.data.fabrications.virtual?.forEach((v) => {
      const fabId = v.fab_id || fabIds[v.type];
      price += fabricationPrice(item, v, fabId);
    });

    item.data.fabrications.additive?.forEach((a) => {
      const fabId = a.fab_id || fabIds[a.type];
      price += fabricationPrice(item, a, fabId);
    });

    return price;
  }

  function getEdgeTreatmentPrice(item, type, edgePrices) {
    if (!item?.data.fabrications?.edge_treatment) return 0;

    let price = 0;
    item.data.fabrications.edge_treatment.forEach((id, edgeIndex) => {
      const e = edgePrices[id];
      if (e?.unit_price && item.width && item.height) {
        const len = item.cache.edge_lengths[edgeIndex] || 0;
        if (e.unit === "in") {
          price += e.unit_price * len;
        } else if (e.unit === "ft") {
          price += e.unit_price * (len / 12);
        } else if (e.unit === "m") {
          price += e.unit_price * (len / 39.3701);
        } else if (e.unit === "each") {
          price += e.unit_price;
        }
      }
    });
    return price;
  }

  function computePrice(item, type, entry, fabPrices, etPrices) {
    if (!item || !entry) return 0;

    if (entry.unit === "Mixed") {
      return null;
    }

    if (!Number.isFinite(entry.unit_price)) {
      return null;
    }

    let price = getPreFabricationPrice(item, entry);
    const fabPrice = getItemFabPrice(item, fabPrices);
    const etPrice = getEdgeTreatmentPrice(item, type, etPrices);

    return price + fabPrice + etPrice;
  }

  function getUnitOptions(entry) {
    if (entry.unit === "Mixed") {
      return ["Mixed", ...unitOptions].map((uo) => ({ label: unitOptionLabels[uo], value: uo }));
    }

    return unitOptions.map((uo) => ({ label: unitOptionLabels[uo], value: uo }));
  }

  function getScUnitOptions(entry) {
    if (entry.unit === "Mixed") {
      return ["Mixed", ...scUnitOptions].map((uo) => ({ label: unitOptionLabels[uo], value: uo }));
    }

    return scUnitOptions.map((uo) => ({ label: unitOptionLabels[uo], value: uo }));
  }

  function getSurchargedPriceTiers(surcharges) {
    return surcharges.map((s) => {
      const prods =
        s.data?.affected_products.reduce((acc, pid) => {
          acc[pid] = 1;
          return acc;
        }, {}) || {};

      if (s.data?.tiers) {
        s.data.tiers.forEach((tier) => {
          tier.affected_products.forEach((pid) => {
            if (prods[pid] != null) {
              prods[pid] *= tier.multiplier;
            }
          });
        });
      }

      return prods;
    });
  }

  function getScPrices(item, type, surcharges, itemPrice, scPrices) {
    if (!item || !scPrices) return {};

    const scp = {};

    surcharges.forEach((s, i) => {
      const prods = surchargedProds[i];
      if (!prods[type?.product_id]) {
        scp[s.id] = 0;
      }

      const entry = scPrices[i];

      let price = getItemSurchargePrice(item, entry, itemPrice);

      scp[s.id] = price;
    });

    return scp;
  }

  function getTotalPrice(item, itemPrice, surcharges, isPrices) {
    const scPrices = surcharges.reduce((s, sc) => s + (isPrices[sc.id] || 0), 0);
    return itemPrice * item.quantity + scPrices;
  }

  function getFabUnitOptions(fab) {
    if (["two-point", "edge-aligned"].includes(fab.position)) {
      return ["each", "in", "ft", "m"];
    }
    return ["each"];
  }

  function apply() {
    const updates = selectedProductList.map((item) => {
      const type = $group?.types[item.type_id];
      const itemPrice = computePrice(item, type, entry, fabPrices, etPrices);
      const diff = {
        price_override: itemPrice,
        price_override_basis: cloneDeep(entry),
      };

      if (surcharges.length) {
        const isPrices = getScPrices(item, type, surcharges, itemPrice, scPrices);
        diff.surcharge_price_overrides = surcharges.map((s) => isPrices[s.id] ?? null);
      }

      return {
        type: "item",
        id: item.id,
        diff: {
          price_override: itemPrice,
          price_override_basis: cloneDeep(entry),
        },
      };
    });

    group.update(updates);
  }

  function reset() {
    const updates = selectedProductList.map((item) => ({
      type: "item",
      id: item.id,
      prop: "price_override",
      value: null,
    }));

    group.update(updates);
  }

  function applySurcharge(index) {
    const updates = selectedProductList.map((item) => {
      const type = $group?.types[item.type_id];
      const itemPrice = itemPrices[item.id] || 0;

      const isPrices = getScPrices(item, type, surcharges, itemPrice, scPrices);

      const po = surcharges.map((_, sIdx) => item.surcharge_price_overrides?.[sIdx] ?? null);
      const surcharge = surcharges[index];
      po[index] = isPrices[surcharge.id] ?? null;
      const basis = surcharges.map((_, sIdx) => item.surcharge_price_override_basis?.[sIdx] ?? null);
      basis[index] = scPrices[index];

      return {
        type: "item",
        id: item.id,
        diff: {
          surcharge_price_overrides: po,
          surcharge_price_override_basis: basis,
        },
      };
    });

    group.update(updates);
  }

  function resetSurcharge(index) {
    const updates = selectedProductList.map((item) => {
      const po = surcharges.map((_, sIdx) => item.surcharge_price_overrides?.[sIdx] ?? null);
      const basis = surcharges.map((_, sIdx) => item.surcharge_price_override_basis?.[sIdx] ?? null);
      po[index] = null;
      basis[index] = null;

      return {
        type: "item",
        id: item.id,
        diff: {
          surcharge_price_overrides: po,
          surcharge_price_override_basis: basis,
        },
      };
    });

    group.update(updates);
  }
</script>

<div class="border-l border-gray-300 p-4 space-y-4 price-computer text-xs h-full">
  <div class="flex justify-between items-center">
    <h3>Price Calculator</h3>
  </div>
  <div>
    Product List: <span class="font-bold" class:italic={!productList}
      >{productList?.name || "None selected"}</span>
  </div>
  <div>
    Currency: <span class="font-bold">{productList?.currency || ""}</span>
  </div>

  <div class="space-y-2">
    <TextInput label="Unit Price" border {formatter} {parser} bind:value={entry.unit_price} />
    <SelectInput
      label="Price Unit"
      border
      bind:value={entry.unit}
      options={getUnitOptions(entry)}
      on:input={() => (entry.unit_price = null)} />
  </div>

  {#if entry?.fabrication_price_entries.length}
    <div class="space-y-2">
      <div class="font-bold">Fabrication Prices:</div>
      <div class="space-y-2">
        {#each entry.fabrication_price_entries as fabPriceEntry, fabIndex}
          {@const fab =
            $fabrications[fabPriceEntry.fabrication_id] ||
            $group?.data.fabrications?.[fabPriceEntry.fabrication_id]}
          {@const unitOptions = getFabUnitOptions(fab).map((o) => ({ label: o, value: o }))}
          <div class="flex gap-2 items-center">
            <div class="flex-none">
              {fabPriceEntry.quantity} x
            </div>
            <div class="w-1/3 flex-none truncate">
              {fab?.name}
            </div>
            <div class="grow flex gap-2">
              <TextInput label="$" border {formatter} {parser} bind:value={fabPriceEntry.unit_price} />
              <SelectInput label="/" border bind:value={fabPriceEntry.unit} options={unitOptions} />
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  {#if entry.edge_treatment_price_entries.length}
    <div>
      <div class="space-y-2">
        <div class="font-bold">Edge Treatment Prices:</div>
        <div class="space-y-2">
          {#each entry.edge_treatment_price_entries as edgePriceEntry, etIndex}
            {@const edge = $edgeTreatments[edgePriceEntry.edge_treatment_id]}
            <div class="flex pl-4 gap-2 items-center">
              <div class="w-1/3 flex-none">
                <div class="w-full truncate">
                  {edge?.name}
                </div>
              </div>
              <TextInput label="$" border {formatter} {parser} bind:value={edgePriceEntry.unit_price} />
              <SelectInput
                label="/"
                border
                bind:value={edgePriceEntry.unit}
                options={[
                  { label: "in", value: "in" },
                  { label: "ft", value: "ft" },
                  { label: "m", value: "m" },
                ]}
                on:input={() => (edgePriceEntry.unit_price = null)} />
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  <div class="space-y-2">
    <div class="font-bold">Computed Prices:</div>
    {#if selectedProductList.length}
      {#each selectedProductList as item}
        {@const type = $group?.types[item.type_id] || {}}
        {@const itemPrice = computePrice(item, type, entry, fabPrices, etPrices)}
        {@const index = itemIndices[item.id] ?? 0}
        <div class="pl-8 flex gap-2">
          <div class="flex-none w-6">{index + 1}</div>
          <div class="grow truncate">{type.name || ""}</div>
          <div class="font-bold flex-none">
            {itemPrice == null
              ? "N/A"
              : `${itemPrice.toLocaleString("en-US", { style: "currency", currency: productList?.currency || "USD" })}`}
          </div>
        </div>
      {/each}
    {:else}
      <div class="ml-4 italic text-gray-500">None selected.</div>
    {/if}
  </div>

  <div class="flex items-center gap-2 justify-end">
    <button class="btn" on:click={reset}>Reset to Default</button>
    <button
      class="btn btn-primary"
      on:click={apply}
      disabled={entry.unit === "Mixed" || !Number.isFinite(entry.unit_price)}>Apply</button>
  </div>

  {#if surcharges.length}
    {#each surcharges as surcharge, index}
      {#key surcharge.id}
        {#if selectedSurcharges[surcharge.id]}
          <div class="space-y-2">
            <div class="font-bold">{surcharge.name}</div>
            <div class="flex gap-2">
              <TextInput label="Price" border {formatter} {parser} bind:value={scPrices[index].unit_price} />
              <SelectInput
                label="Unit"
                border
                bind:value={scPrices[index].unit}
                options={getScUnitOptions(scPrices[index])}
                on:input={() => (scPrices[index].unit_price = null)} />
            </div>
          </div>

          <div class="flex items-center gap-2 justify-end">
            <button class="btn" on:click={() => resetSurcharge(index)}>Reset to Default</button>
            <button
              class="btn btn-primary"
              on:click={() => applySurcharge(index)}
              disabled={entry.unit === "Mixed" || !Number.isFinite(entry.unit_price)}>Apply</button>
          </div>
        {/if}
      {/key}
    {/each}
  {/if}
</div>
